// V9.25 · Custo por adquirente: quanto a escola pagou de taxa em cada máquina
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { CreditCard, Lightbulb, TrendingDown } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const METODO = {
  pix: 'PIX', cash: 'Dinheiro', card_debit: 'Débito', card_credit: 'Crédito',
  pix_card: 'PIX no cartão', boleto: 'Boleto', transfer: 'Transferência', outro: 'Outro',
};

export const AdquirentesTab = () => {
  const anos = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1, y - 2];
  }, []);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/fin/ledger/acquirer-costs', { params: { year } });
      setData(d);
    } catch (_) { toast.error('Erro ao carregar o custo por adquirente.'); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const maxTaxa = Math.max(1, ...(data?.itens || []).map((i) => i.taxa));
  const maxMes = Math.max(1, ...(data?.por_mes || []).map((m) => m.taxa));

  return (
    <div className="space-y-3" data-testid="tab-adquirentes">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="adq-ano"><SelectValue /></SelectTrigger>
          <SelectContent>{anos.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {data?.transacoes || 0} transação(ões) com taxa em {year}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-xl border p-4 border-rose-300 bg-rose-50/60" data-testid="adq-total-taxa">
          <p className="text-xs font-bold uppercase text-muted-foreground">Taxa paga no ano</p>
          <p className="text-2xl font-bold tabular-nums">{money(data?.total_taxa)}</p>
        </div>
        <div className="rounded-xl border p-4" data-testid="adq-volume">
          <p className="text-xs font-bold uppercase text-muted-foreground">Volume processado</p>
          <p className="text-2xl font-bold tabular-nums">{money(data?.total_bruto)}</p>
        </div>
        <div className="rounded-xl border p-4 border-amber-300 bg-amber-50/60" data-testid="adq-taxa-media">
          <p className="text-xs font-bold uppercase text-muted-foreground">Taxa média</p>
          <p className="text-2xl font-bold tabular-nums">{data?.taxa_media_percent ?? 0}%</p>
          {data?.melhor_taxa && (
            <p className="text-xs text-muted-foreground">
              Melhor: {data.melhor_taxa.adquirente} · {data.melhor_taxa.taxa_media_percent}%
            </p>
          )}
        </div>
        <div className="rounded-xl border p-4 border-emerald-300 bg-emerald-50/60" data-testid="adq-economia">
          <p className="text-xs font-bold uppercase text-muted-foreground">Economia possível</p>
          <p className="text-2xl font-bold tabular-nums">{money(data?.economia_potencial)}</p>
          <p className="text-xs text-muted-foreground">migrando o volume para a melhor taxa</p>
        </div>
      </div>

      {data?.insight && (
        <Card className="border-sky-300 bg-sky-50/60" data-testid="adq-insight">
          <CardContent className="p-4 flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-sky-700 shrink-0" />
            <p className="text-sm">{data.insight}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Por adquirente / máquina
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Adquirente</TableHead>
              <TableHead className="text-right">Volume</TableHead>
              <TableHead className="text-right">Taxa paga</TableHead>
              <TableHead className="text-right">Taxa média</TableHead>
              <TableHead className="text-right">Transações</TableHead>
              <TableHead className="text-right">Ticket médio</TableHead>
              <TableHead className="w-28">Peso</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center py-8">Carregando…</TableCell></TableRow>}
              {!loading && !data?.itens?.length && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  Nenhuma taxa registrada em {year}. As taxas aparecem aqui automaticamente a cada
                  pagamento no cartão (configure a matriz em Contas Bancárias).
                </TableCell></TableRow>
              )}
              {!loading && (data?.itens || []).map((i) => (
                <React.Fragment key={i.adquirente}>
                  <TableRow data-testid={`adq-row-${i.adquirente}`}>
                    <TableCell className="font-medium">{i.adquirente}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(i.bruto)}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold text-rose-700">{money(i.taxa)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant="outline" className={i.adquirente === data?.melhor_taxa?.adquirente
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : 'bg-amber-100 text-amber-800 border-amber-300'}>
                        {i.taxa_media_percent}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{i.transacoes}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(i.ticket_medio)}</TableCell>
                    <TableCell>
                      <div className="h-2 w-24 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-rose-500 rounded-full"
                          style={{ width: `${(i.taxa / maxTaxa) * 100}%` }} />
                      </div>
                    </TableCell>
                  </TableRow>
                  {i.por_metodo.map((m) => (
                    <TableRow key={`${i.adquirente}-${m.metodo}`} className="bg-muted/30">
                      <TableCell className="pl-8 text-xs text-muted-foreground">
                        {METODO[m.metodo] || m.metodo}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{money(m.bruto)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{money(m.taxa)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{m.taxa_media_percent}%</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{m.transacoes}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!!data?.por_mes?.length && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Taxa paga mês a mês
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-end gap-2 h-40">
            {data.por_mes.map((m) => (
              <div key={m.competencia} className="flex-1 flex flex-col items-center justify-end gap-1"
                data-testid={`adq-mes-${m.competencia.replace('/', '-')}`}>
                <span className="text-[10px] tabular-nums">{money(m.taxa)}</span>
                <div className="w-full bg-rose-500/80 rounded-t transition-all hover:bg-rose-600"
                  style={{ height: `${Math.max(4, (m.taxa / maxMes) * 100)}%` }} />
                <span className="text-[10px] text-muted-foreground">{m.competencia.slice(0, 2)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdquirentesTab;
